import styled from 'styled-components';

import { Icon } from '@/ui/Icon';
import { formatToHoursAndMinutesFromSeconds, utcToLocalDateСustomFormat } from '@/utils/time';
import { LearningJourney } from '@/store/v2/journeys/journey';
import { ParticipationV3 } from '@/store/v2/catalog-v2/model/participation';
import { LinearProgressBar } from '@/ui/ProgressBar';
import { RLearningItemAvailabilityType, RLearningJourneyCompletionStatus, RLearningType } from '@/store/v2/remote.gen';
import { LearningCardProps, LearningCard } from '@/administration/pages/Journey/common/atoms/LearningCard/LearningCard';
import { Button } from '@/ui/Button';
import { Tooltip } from '@/ui/Tooltip';
import { Skeleton } from '@/ui/Skeleton';
import { PARENT_JOURNEY_ID_SEARCH_KEY } from '@/store/v2/journeys/useLearningJourneyBreadcrumbs';
import { UpNextAllItemsLockedInformer } from '@/features/journey/admin/atoms/UpNextAllItemsLockedInformer';
import { obligatoryItemsLabel } from '@/features/journey/admin/utils/obligatoryItemsLabel';

const RootStyled = styled.div`
  padding: 32px;
  border: 1px solid ${({ theme }) => theme.colors.neutral.outline1};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;

const Separator = styled.div`
  width: 100%;
  height: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.neutral.outline1};
  margin: 32px 0;
`;

const TitleStyled = styled.p`
  ${({ theme }) => theme.typography.title3};
  color: ${(p) => p.theme.colors.neutral.onBackground};
  margin: 0 0 24px 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const MetaBlockStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MetaItemStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconStyled = styled(Icon)`
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const MetaLabelStyled = styled.div`
  ${({ theme }) => theme.typography.subheadline};
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const ProgressCaption = styled.div`
  ${({ theme }) => theme.typography.caption};
  color: ${(p) => p.theme.colors.neutral.outline2};
  margin: 8px 0 0 0;
`;

type JourneyViewProgressBoxProps = {
  journey?: LearningJourney;
  requiredItemsCount?: number;
  progress?: ParticipationV3;

  nextLearningItem?: LearningCardProps;
  nextLearningItemLoading?: boolean;
  /** From journey API: when true, show the all-locked up-next informer instead of the next-item card. */
  allUpcomingItemsLocked?: boolean;
  /** Availability date of the item that currently blocks journey progress. */
  allUpcomingItemsUnlockAt?: Date | string;
};

export const JourneyViewProgressBox = ({
  requiredItemsCount,
  progress,
  journey,
  nextLearningItem,
  nextLearningItemLoading,
  allUpcomingItemsLocked,
  allUpcomingItemsUnlockAt,
}: JourneyViewProgressBoxProps): React.ReactElement => {
  const completedPercentage = (progress?.progress || 0) * 100;
  const completedAt = progress?.completedAt;
  const showRequiredItemsCount = journey?.completionCriteria !== RLearningJourneyCompletionStatus.InvestTime;
  const showCompletionInvestTime = journey?.completionCriteria === RLearningJourneyCompletionStatus.InvestTime;

  const isNextItemEvent = [RLearningType.LiveEvent, RLearningType.Seminar, RLearningType.Webinar].includes(
    nextLearningItem?.type as RLearningType,
  );
  const isNextItemAvailable =
    nextLearningItem?.availabilityType !== RLearningItemAvailabilityType.OnSchedule ||
    new Date(nextLearningItem?.availabilityScheduledAt || new Date()).getTime() < new Date().getTime();

  return (
    <RootStyled>
      <TitleStyled>
        Journey progress{' '}
        <Tooltip title="Updates make take up to 72 hours" placement="bottom">
          <Icon icon={['far', 'circle-info']} size="xs" />
        </Tooltip>
      </TitleStyled>
      <MetaBlockStyled>
        <div>
          <LinearProgressBar
            size="lg"
            color="gradient"
            value={completedPercentage}
            maxValue={100}
            roundedBar={false}
            labelFormat="percentage"
            roundedTrack
            showLabel
          />
          {journey?.completionCriteria === RLearningJourneyCompletionStatus.CompleteAllItems && (
            <ProgressCaption>Finish all obligatory items to complete</ProgressCaption>
          )}
          {journey?.completionCriteria === RLearningJourneyCompletionStatus.InvestTime && (
            <ProgressCaption>
              Invest {formatToHoursAndMinutesFromSeconds(journey.completionInvestTimeSec || 0)} to complete
            </ProgressCaption>
          )}
        </div>
        {completedAt && (
          <MetaItemStyled>
            <IconStyled icon={['fas', 'check']} />
            <MetaLabelStyled>Completed on {utcToLocalDateСustomFormat(completedAt, 'YYYY-MM-DD')}</MetaLabelStyled>
          </MetaItemStyled>
        )}
        {showRequiredItemsCount && (
          <MetaItemStyled>
            <IconStyled icon={['far', 'route']} />
            <MetaLabelStyled>{obligatoryItemsLabel(requiredItemsCount ?? 0)}</MetaLabelStyled>
          </MetaItemStyled>
        )}
        {showCompletionInvestTime && (
          <MetaItemStyled>
            <IconStyled icon={['far', 'clock']} />
            <MetaLabelStyled>{formatToHoursAndMinutesFromSeconds(journey?.completionInvestTimeSec || 0)} total</MetaLabelStyled>
          </MetaItemStyled>
        )}
      </MetaBlockStyled>
      {!progress?.isCompleted && (
        <>
          {allUpcomingItemsLocked ? (
            <>
              <Separator />
              <UpNextAllItemsLockedInformer unlockAt={allUpcomingItemsUnlockAt} />
            </>
          ) : (
            <>
              <Separator />
              {nextLearningItemLoading && <Skeleton height={124} />}
              {nextLearningItem && !nextLearningItemLoading && (
                <MetaBlockStyled>
                  <LearningCard {...nextLearningItem} variant="compact" />
                  {isNextItemAvailable && (
                    <Button
                      variant={'primary'}
                      icon={isNextItemEvent ? undefined : ['far', 'play']}
                      $iconPosition="start"
                      to={
                        isNextItemEvent && journey?.id
                          ? {
                              pathname: nextLearningItem.launchPath || '',
                              search: `${PARENT_JOURNEY_ID_SEARCH_KEY}=${journey.id}`,
                            }
                          : nextLearningItem.launchPath
                      }
                    >
                      {!isNextItemEvent && (!!nextLearningItem.progress ? 'Continue' : 'Start')}
                      {isNextItemEvent && 'View details'}
                    </Button>
                  )}
                </MetaBlockStyled>
              )}
            </>
          )}
        </>
      )}
    </RootStyled>
  );
};
